import { useTheme } from '@mui/material/styles';
import type * as ChartJs from 'chart.js';
import dayjs from 'dayjs';
import React from 'react';
import { Line } from 'react-chartjs-2';

import { useIsMobile } from '@hooks/useIsMobile';

import { Interval } from '@models/graph';

import useAspectRatio from '../hooks/useAspectRatio';
import '../register';

export type Dataset = ChartJs.ChartDataset<'line', ChartJs.Point[]>;
export type Plugin = ChartJs.Plugin<'line', ChartJs.Point[]>;
export type ChartData = ChartJs.ChartData<'line', ChartJs.Point[]>;

interface LineChartProps {
  chartData: ChartData;
  plugins: Plugin[];
  formatValue?: (value: number) => string;
  options?: LineChartOptions;
  minDate: Date;
  maxDate: Date;
  interval: Interval;
}

interface LineChartOptions {
  colors?: LineChartColorOptions;
  normalRange?: { lower: number; upper: number; unit: string }[];
  xTimeUnit?: ChartJs.TimeUnit;
  customTooltipLabel?: (
    tooltipItem: ChartJs.TooltipItem<'line'>
  ) => string | undefined;
  /** Label the y axis with `formatValue` (e.g. "5' 8"" rather than "68.11"). */
  formatYAxisTicks?: boolean;
}

export interface LineChartColorOptions {
  font?: string;
  grid?: string;
}

function getTimeUnit(interval: Interval): ChartJs.TimeUnit {
  switch (interval) {
    case Interval.Year:
      return 'month';
    case Interval.Month:
      return 'week';
    case Interval.Week:
      return 'day';
    case Interval.Day:
    default:
      return 'hour';
  }
}

function formatTooltipTitle(date: number, interval: Interval) {
  let title = '';
  switch (interval) {
    case Interval.Year:
      title = dayjs(date).format('MMMM YYYY');
      break;
    case Interval.Month:
      title = `Week of ${dayjs(date).startOf('week').format('ll')}`;
      break;
    case Interval.Week:
      title = dayjs(date).format('ll');
      break;
    case Interval.Day:
    default:
      title = dayjs(date).format('lll');
      break;
  }
  return title;
}

function LineChart({
  chartData,
  plugins,
  formatValue,
  options: customOptions,
  interval,
  maxDate,
  minDate,
}: LineChartProps) {
  const mobile = useIsMobile();
  const aspectRatio = useAspectRatio();

  const { palette, typography } = useTheme();
  const defaultDatasetConfig: ChartJs.ChartDataset<'line', ChartJs.Point[]> = {
    pointRadius: 4,
    pointBorderWidth: 0.1,
    pointHoverBorderWidth: 0.1,
    backgroundColor: palette.purple.main,
    borderColor: customOptions?.colors?.font ?? palette.grey['400'],
    borderWidth: 2,
    data: [],
    cubicInterpolationMode: 'monotone',
    pointStyle: 'circle',
  };

  const data: ChartJs.ChartData<'line', ChartJs.Point[]> = {
    datasets: chartData.datasets.map((dataset) => ({
      ...defaultDatasetConfig,
      ...dataset,
    })),
  };

  const options: ChartJs.ChartOptions<'line'> = {
    maintainAspectRatio: true,
    aspectRatio: aspectRatio,
    animations: {
      x: {
        delay: 0,
        duration: 600,
      },
      y: {
        delay: 0,
        duration: 600,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => {
            const customTooltipLabel =
              customOptions?.customTooltipLabel?.(tooltipItem);
            if (customTooltipLabel) {
              return customTooltipLabel;
            }
            const label = tooltipItem.dataset.label ?? '';
            const value = formatValue
              ? formatValue(Number(tooltipItem.parsed.y))
              : tooltipItem.formattedValue;
            return `${label}: ${value}`;
          },
          title: (items) => {
            const [item] = items;
            const date = item.parsed.x;
            return formatTooltipTitle(date || 0, interval);
          },
        },
      },
      ...(customOptions?.normalRange && {
        'normal-range-plugin': {
          range: customOptions.normalRange,
        },
      }),
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: customOptions?.xTimeUnit ?? getTimeUnit(interval),
        },
        min: minDate.getTime(),
        max: maxDate.getTime(),
        ticks: {
          font: {
            size: 12,
            family: typography.fontFamily,
          },
          color: customOptions?.colors?.font ?? palette.text.secondary,
          minRotation: mobile ? 20 : 0,
        },
        grid: {
          color: customOptions?.colors?.grid ?? palette.grey['100'],
        },
      },
      y: {
        // Without grace, Chart.js sets the axis bounds to the exact data
        // min/max, so the most extreme point renders flush against the plot
        // edge — most visible when values are far apart (VEG-771).
        grace: '5%',
        ticks: {
          font: {
            size: 12,
            family: typography.fontFamily,
          },
          color: customOptions?.colors?.font ?? palette.text.secondary,
          ...(customOptions?.formatYAxisTicks &&
            formatValue && {
              callback: (tickValue: number | string) =>
                formatValue(Number(tickValue)),
            }),
        },
        grid: {
          color: customOptions?.colors?.grid,
        },
      },
    },
  };

  return <Line data={data} options={options} plugins={plugins} />;
}

export default LineChart;
